'use client'

// THEME COMPLIANCE: All UI implementations must support both dark and light themes.

import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import { gsap } from 'gsap'
import EnhancedNavigation from './navigation/EnhancedNavigation'
import LandingNavigation from '@/components/landing/LandingNavigation'

export default function Navigation() {
  const pathname = usePathname()
  const { user, isPhoneVerified } = useAuth()
  const logoRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (logoRef.current) {
        // Subtle floating animation
        gsap.to(logoRef.current, {
          y: -5,
          duration: 2,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true
        })

        // Interactive hover animation
        logoRef.current.addEventListener('mouseenter', () => {
          gsap.to(logoRef.current, {
            scale: 1.1,
            duration: 0.3,
            ease: 'power2.out'
          })
        })

        logoRef.current.addEventListener('mouseleave', () => {
          gsap.to(logoRef.current, {
            scale: 1,
            duration: 0.3,
            ease: 'power2.out'
          })
        })
      }
    },
    { scope: logoRef }
  )

  // Determine navigation context
  const isLandingPage = pathname === '/' && !user
  const isAuthenticatedApp = user && isPhoneVerified

  if (isLandingPage) {
    return <LandingNavigation />
  }

  if (isAuthenticatedApp) {
    return <EnhancedNavigation />
  }

  // For all other cases, fall back to a minimal navigation shell
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-background/90 text-foreground backdrop-blur-2xl shadow-2xl shadow-blue-500/10 drop-shadow-[0_0_15px_rgba(59,130,246,0.15)] pointer-events-auto dark:border-primary/20 dark:bg-slate-950/90">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 py-2">
          {/* Logo */}
          <div ref={logoRef} className="relative group flex items-center">
            <Link
              href={isAuthenticatedApp ? '/dashboard' : '/'}
              className="flex items-center space-x-3"
            >
              {/* Removed glow background */}
              <Image
                src="/assets/Logos/NPFCBlackLogo.png"
                alt="Fantasy XI Logo"
                width={110}
                height={110}
                className="relative z-50 object-contain block dark:hidden"
                style={{ width: '100px', height: '100px' }}
              />
              <Image
                src="/assets/Logos/NPFCwhiteLogo.png"
                alt="Fantasy XI Logo"
                width={110}
                height={110}
                className="relative z-50 object-contain hidden dark:block"
                style={{ width: '100px', height: '100px' }}
              />
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}
